'use client';

import { Close as CancelIcon } from '@mui/icons-material';
import { Button, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { InverseButton } from '@parkchirp/ui-library';
import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import { AccessDeviceFeesForRates, BillingProfile, FeeSelection, FeesForRates } from '../../../types';
import { AccessDeviceCodeAutoComplete } from './accessDeviceCodeAutoComplete';
import { AccessDeviceFeeAutoComplete } from './accessDeviceFeeAutoComplete';
import { FeeAutoComplete } from './feeAutoComplete';
import { ProductRateCodeAutoComplete } from './productRateCodeAutoComplete';

interface Props {
	setTabValue: Dispatch<SetStateAction<string>>;
	setAccessDeviceMessage: Dispatch<SetStateAction<string>>;
	accessDeviceMessage: string;
	setActiveStep: Dispatch<SetStateAction<number>>;
	activationFee: FeeSelection;
	setActivationFee: Dispatch<SetStateAction<FeeSelection>>;
	transactionFee: FeeSelection;
	setTransactionFee: Dispatch<SetStateAction<FeeSelection>>;
	lateFee: FeeSelection;
	setLateFee: Dispatch<SetStateAction<FeeSelection>>;
	feeOptions: FeesForRates[];
	setAccessDeviceFees: Dispatch<SetStateAction<AccessDeviceFeesForRates[]>>;
	setFeeOptions: Dispatch<SetStateAction<FeesForRates[]>>;
	accessDeviceFees: AccessDeviceFeesForRates[];
	setAvailableAccessDeviceTypes: Dispatch<SetStateAction<any[]>>;
	lateFeeDay: number;
	setLateFeeDay: Dispatch<SetStateAction<number>>;
	availableAccessDeviceTypes: any[];
	isChild: boolean;
	billingProfile: BillingProfile;
	theme: any;
	facilityIntegration: string;
	online: string;
}

const defaultFeesHistory = {
	productCode: {
		code: null,
	},
	rateCode: {
		code: null,
	},
};

const getUpdatedDeviceType = (data) => ({
	feeName: '',
	feesHistory: defaultFeesHistory,
	id: '',
	type: data.name || '',
	feeAccessDeviceType: {
		accessDeviceType: {
			type: data.name || '',
		},
		accessDeviceTypeId: data.id || '',
	},
});

const getUpdatedFee = (data) => ({
	feeName: data?.feeName || '',
	feesHistory: data?.feesHistory || defaultFeesHistory,
	id: data?.id || '',
	type: data?.feeAccessDeviceType?.accessDeviceType?.type || '',
	feeAccessDeviceType: {
		accessDeviceType: {
			type: data?.feeAccessDeviceType?.accessDeviceType?.type || '',
		},
		accessDeviceTypeId: data?.feeAccessDeviceType?.accessDeviceTypeId || '',
	},
});

const daysOfTheMonth = [
	{ value: 1, label: '1st' },
	{ value: 2, label: '2nd' },
	{ value: 3, label: '3rd' },
	{ value: 4, label: '4th' },
	{ value: 5, label: '5th' },
	{ value: 6, label: '6th' },
	{ value: 7, label: '7th' },
	{ value: 8, label: '8th' },
	{ value: 9, label: '9th' },
	{ value: 10, label: '10th' },
	{ value: 11, label: '11th' },
	{ value: 12, label: '12th' },
	{ value: 13, label: '13th' },
	{ value: 14, label: '14th' },
	{ value: 15, label: '15th' },
	{ value: 16, label: '16th' },
	{ value: 17, label: '17th' },
	{ value: 18, label: '18th' },
	{ value: 19, label: '19th' },
	{ value: 20, label: '20th' },
	{ value: 21, label: '21st' },
	{ value: 22, label: '22nd' },
	{ value: 23, label: '23rd' },
	{ value: 24, label: '24th' },
	{ value: 25, label: '25th' },
	{ value: 26, label: '26th' },
	{ value: 27, label: '27th' },
	{ value: 28, label: '28th' },
];

const Fees: FC<Props> = ({
	setTabValue,
	accessDeviceFees,
	setAccessDeviceFees,
	accessDeviceMessage,
	setAccessDeviceMessage,
	setActiveStep,
	activationFee,
	setActivationFee,
	feeOptions,
	setFeeOptions,
	transactionFee,
	setTransactionFee,
	setAvailableAccessDeviceTypes,
	availableAccessDeviceTypes,
	isChild,
	lateFee,
	setLateFee,
	lateFeeDay,
	setLateFeeDay,
	billingProfile,
	theme,
	facilityIntegration,
	online,
}) => {
	const [missingFields, setMissingFields] = useState<string>('');

	const handleNext = () => {
		let errorMessage = '';
		if (accessDeviceFees.length > 0) {
			accessDeviceFees.forEach((ad) => {
				if (!ad.type) {
					errorMessage += 'accessDeviceType,';
				}
			});
		}
		if (
			facilityIntegration === 'GET_MY_PARKING' &&
			accessDeviceFees.length === 0 &&
			online !== 'ADMIN_SIGN_UP_ONLY'
		) {
			errorMessage += 'gmpError,';
		} else {
			const hasRequiredBarcode = accessDeviceFees.some(
				(ad) => ad.feeAccessDeviceType.accessDeviceTypeId === 2 && ad.required,
			);
			if (!hasRequiredBarcode && facilityIntegration === 'GET_MY_PARKING' && online !== 'ADMIN_SIGN_UP_ONLY') {
				errorMessage += 'gmpError,';
			}

			accessDeviceFees.forEach((ad) => {
				if (!ad.type) {
					errorMessage += 'accessDeviceType is missing,';
				}
			});
		}
		if (lateFee && !lateFeeDay) {
			errorMessage += 'lateFeeDay,';
		}
		if (errorMessage) {
			setMissingFields(errorMessage);
		} else {
			setMissingFields('');
			setTabValue(isChild ? '6' : '5');
			setActiveStep(isChild ? 5 : 4);
		}
	};

	const handleRemoveRowForAccessDevices = (index: number) => {
		accessDeviceFees.splice(index, 1);
		setAccessDeviceFees([...accessDeviceFees]);
	};

	useEffect(() => {
		const updatedFeeOptions = feeOptions.map((fee) => {
			const isDisabled = accessDeviceFees.some(
				(ad) =>
					ad.feeName === fee.feeName ||
					ad.feeAccessDeviceType?.accessDeviceTypeId === fee.feeAccessDeviceType?.accessDeviceTypeId,
			);
			return {
				...fee,
				disabled: isDisabled,
			};
		});

		const updatedAccessDeviceTypeOptions = availableAccessDeviceTypes.map((fee) => {
			const isDisabled = accessDeviceFees.some((ad) => ad.type === fee.name);
			return {
				...fee,
				disabled: isDisabled,
			};
		});
		setFeeOptions(updatedFeeOptions);
		setAvailableAccessDeviceTypes(updatedAccessDeviceTypeOptions);
	}, [accessDeviceFees]);

	return (
		<Grid spacing={2} container>
			<Grid item xs={6}>
				<InputLabel className='pca__ratesForm-feeTitles'>Activation Fee</InputLabel>
				<FeeAutoComplete
					id='activation-fee-select-menu'
					selectedFee={activationFee}
					feeOptions={feeOptions.filter((f: FeesForRates) => f.feeType.name === 'Activation')}
					disabled={isChild}
					handleChange={setActivationFee}
					error={false}
				/>
			</Grid>

			<Grid item xs={6}>
				<InputLabel className='pca__ratesForm-feeTitles'>Product and Rate Code</InputLabel>
				<ProductRateCodeAutoComplete
					id='product-and-rate-code-select-menu'
					selectedFee={activationFee}
					disabled={isChild}
					feeOptions={feeOptions.filter((f: FeesForRates) => f.feeType.name === 'Activation')}
					handleChange={setActivationFee}
					error={false}
				/>
			</Grid>

			<Grid item xs={12}>
				<InputLabel className='pca__ratesForm-feeTitles'>Access Device Setup</InputLabel>
				{missingFields.includes('gmpError') && (
					<p className='pca__ratesForm-required'>A required barcode is required for Get My Parking</p>
				)}
				{accessDeviceFees.map((ad, index) => (
					<Grid
						className='pca__ratesForm-bottomMargin pca__ratesForm-divider'
						spacing={2}
						container
						key={index}
					>
						<Grid item xs={1}>
							<InputLabel>
								Required<span className='pca__ratesForm-required'>*</span>
							</InputLabel>
							<Select
								fullWidth
								disabled={isChild}
								value={accessDeviceFees[index].required ? 1 : 0}
								onChange={(e) => {
									setAccessDeviceFees((prevState: AccessDeviceFeesForRates[]) => {
										prevState[index].required = Number(e.target.value);
										return [...prevState];
									});
								}}
								data-cy={`access-device-fee-required-${index}`}
							>
								<MenuItem value={1}>Yes</MenuItem>
								<MenuItem value={0}>No</MenuItem>
							</Select>
						</Grid>
						<Grid item xs={2}>
							<InputLabel>
								Access Device Type<span className='pca__ratesForm-required'>*</span>
							</InputLabel>
							<Select
								fullWidth
								required
								disabled={isChild}
								value={accessDeviceFees[index].feeAccessDeviceType.accessDeviceTypeId || ''}
								error={missingFields.includes('accessDeviceType')}
								onChange={(e: any) => {
									const findDeviceType = availableAccessDeviceTypes.find(
										(adType) => adType.id === e.target.value,
									);

									setAccessDeviceFees((prevState) => [
										...prevState.slice(0, index),
										{
											...prevState[index],
											...getUpdatedDeviceType(findDeviceType),
										},
										...prevState.slice(index + 1),
									]);
								}}
								data-cy={`access-device-fee-type-select-menu-${index}`}
							>
								{availableAccessDeviceTypes.map((adType) => (
									<MenuItem
										key={`device-type-${adType.id}`}
										disabled={adType.disabled}
										value={adType.id}
									>
										{adType.name}
									</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item xs={4}>
							<InputLabel>Access Device Fee</InputLabel>
							<AccessDeviceFeeAutoComplete
								id={`access-device-fee-select-menu-${index}`}
								disabled={isChild}
								selectedFee={accessDeviceFees[index]}
								feeOptions={feeOptions.filter(
									(f) =>
										f.feeType.name === 'Access Device' &&
										f.feeAccessDeviceType?.accessDeviceType.type === accessDeviceFees[index].type,
								)}
								handleChange={(_, value) => {
									setAccessDeviceFees((prevState) => [
										...prevState.slice(0, index),
										{
											...prevState[index],
											...getUpdatedFee(value),
										},
										...prevState.slice(index + 1),
									]);
								}}
								error={false}
							/>
						</Grid>
						<Grid item xs={4}>
							<InputLabel>Product And Rate Code</InputLabel>
							<AccessDeviceCodeAutoComplete
								id={`product-and-rate-code-select-menu-${index}`}
								disabled={isChild}
								selectedFee={accessDeviceFees[index]}
								feeOptions={feeOptions.filter(
									(f) =>
										f.feeType.name === 'Access Device' &&
										f.feeAccessDeviceType?.accessDeviceType.type === accessDeviceFees[index].type,
								)}
								handleChange={(_, value) => {
									const findFee = feeOptions.find((fee) => fee.id === value?.key);
									setAccessDeviceFees((prevState) => [
										...prevState.slice(0, index),
										{
											...prevState[index],
											...getUpdatedFee(findFee),
										},
										...prevState.slice(index + 1),
									]);
								}}
								error={false}
							/>
						</Grid>
						<Grid className='pca__ratesForm-centeredGridItem' item xs={1}>
							<div style={{ visibility: 'hidden' }}>Remove</div>
							{!isChild && (
								<CancelIcon
									onClick={() => handleRemoveRowForAccessDevices(index)}
									className='pca__ratesForm-icon'
									fontSize='large'
									data-cy={`access-device-fee-delete-${index}`}
								/>
							)}
						</Grid>
					</Grid>
				))}
				{accessDeviceFees.length < 4 && (
					<Button
						disabled={isChild}
						onClick={() => {
							setAccessDeviceFees((prevState) => [
								...prevState,
								{
									id: '',
									required: 1,
									feeName: '',
									feeTypeId: 1,
									feesHistory: {
										productCode: {
											code: null,
										},
										rateCode: {
											code: null,
										},
									},
									feeType: {
										name: 'Access Device',
									},
									feeAccessDeviceType: {
										accessDeviceTypeId: null,
									},
								},
							]);
						}}
						data-cy='add-new-access-device-fee-button'
					>
						Add Access Device
					</Button>
				)}
			</Grid>
			<Grid item xs={12}>
				<InputLabel>Access Device Message</InputLabel>
				<TextField
					disabled={isChild}
					fullWidth
					onChange={(e) => setAccessDeviceMessage(e.target.value)}
					defaultValue={accessDeviceMessage}
					placeholder='Message'
					inputProps={{ 'data-cy': 'access-device-message-input' }}
				/>
			</Grid>

			<Grid item xs={6}>
				<InputLabel className='pca__ratesForm-feeTitles'>Transaction Fee</InputLabel>
				<FeeAutoComplete
					disabled={isChild}
					id='transaction-fee-select-menu'
					selectedFee={transactionFee}
					feeOptions={feeOptions.filter((f: FeesForRates) => f.feeType.name === 'Transaction')}
					handleChange={setTransactionFee}
					error={false}
				/>
			</Grid>
			<Grid item xs={6}>
				<InputLabel className='pca__ratesForm-feeTitles'>Product and Rate Code</InputLabel>
				<ProductRateCodeAutoComplete
					id='product-and-rate-code-select-menu'
					selectedFee={transactionFee}
					feeOptions={feeOptions.filter((f: FeesForRates) => f.feeType.name === 'Transaction')}
					disabled={isChild}
					handleChange={setTransactionFee}
					error={false}
				/>
			</Grid>
			<Grid item xs={6}>
				<InputLabel className='pca__ratesForm-feeTitles'>Late Fee</InputLabel>
				<FeeAutoComplete
					disabled={isChild}
					id='late-fee-select-menu'
					selectedFee={lateFee}
					feeOptions={feeOptions.filter(
						(f: FeesForRates) => f.feeType.name === 'Late' || f.feeType.name === 'Recurring Late',
					)}
					handleChange={setLateFee}
					error={false}
				/>
			</Grid>
			<Grid item xs={4}>
				<InputLabel className='pca__ratesForm-feeTitles'>Product and Rate Code</InputLabel>
				<ProductRateCodeAutoComplete
					id='product-and-rate-code-select-menu'
					selectedFee={lateFee}
					feeOptions={feeOptions.filter(
						(f: FeesForRates) => f.feeType.name === 'Late' || f.feeType.name === 'Recurring Late',
					)}
					disabled={isChild}
					handleChange={setLateFee}
					error={false}
				/>
			</Grid>
			<Grid item xs={2} className='pca__ratesForm-feeTitles'>
				<InputLabel className='pca__ratesForm-feeTitles'>Late Fee Day</InputLabel>
				<Select
					labelId='day-select-label'
					id='day-select'
					fullWidth
					value={lateFeeDay}
					disabled={isChild}
					error={missingFields.includes('lateFeeDay')}
					onChange={(e) => setLateFeeDay(Number(e.target.value))}
				>
					{daysOfTheMonth
						.filter((day) => day.value > billingProfile.paymentDueDay)
						.map(({ value: itemValue, label }) => (
							<MenuItem key={itemValue} value={itemValue}>
								{label}
							</MenuItem>
						))}
				</Select>
			</Grid>
			<Grid className='pca__ratesForm-containerPadding' spacing={2} container>
				<Grid item xs={6}>
					<InverseButton
						onClick={() => {
							setTabValue(isChild ? '4' : '3');
							setActiveStep(isChild ? 3 : 2);
						}}
						data-cy='previous-button'
						fullWidth
						name='Previous'
						theme={theme}
					/>
				</Grid>
				<Grid item xs={6}>
					<Button fullWidth onClick={handleNext} data-cy='next-step-button'>
						Next
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default Fees;
